import React, { useEffect, useMemo, useRef, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import moment from "moment"
import { Link, useNavigate } from "react-router-dom"
import avatar from "../../../assets/images/avatar.png"
import edit from "../../../assets/images/icons/edit.svg"
import blockIcon from "../../../assets/images/icons/block.svg"
import deleteIcon from "../../../assets/images/icons/delete-red.svg"
import DeleteConfirmationModal from "modals/deleteConfirmation"
import BlockAccessConfirmation from "modals/blockAccessConfirmation"
import { UncontrolledTooltip } from "reactstrap"
import { getAdminsStatus, getTableSortingHeaderIcon } from "helpers/common"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import toast from "react-hot-toast"
import copy from "../../../assets/images/icons/copy.svg"
import { copyToClipboard } from "helpers/utils"
import {
  useBlockInvestorUserMutation,
  useDeleteInvestorUserMutation,
  useLazyGetInvestorUsersQuery,
  useUnblockInvestorUserMutation,
} from "store/api/investorapi/user.api"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const InvestorUsersTable = () => {
  const [getResults, { isFetching }] = useLazyGetInvestorUsersQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isToast, setIsToast] = useState(false)
  const [isBlockToast, setIsBlockToast] = useState(false)
  const [onDelete, { isLoading: isDeleting }] = useDeleteInvestorUserMutation()
  const [onBlock, { isLoading: isBlocking }] = useBlockInvestorUserMutation()
  const [onUnBlock, { isLoading: isUnBlocking }] =
    useUnblockInvestorUserMutation()
  const history = useNavigate()
  const deleteRef = useRef()
  const blockRef = useRef()
  const { hasAnyPermission, hasPermission } = usePermission()
  //meta title
  document.title = "Admins | Prachay"

  useEffect(() => {
    getList()
  }, [filters])

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  const onActionSuccess = () => {
    setIsToast(false)
    deleteRef.current = null

    setIsBlockToast(false)
    blockRef.current = null
    setFilters((prev) => ({ ...prev, page: 1, timestamp: moment().unix() }))
  }

  const columns = useMemo(() => {
    const data = [
      {
        header: ({ column }) =>
          getTableSortingHeaderIcon(column, "User Details"),
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <DefaultAvatar
            email={row?.original?.email}
            name={row?.original?.name}
          />
        ),
      },
      {
        header: () => <div className="flex items-center gap-2">Date</div>,
        accessorKey: "formatted_created_at",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {getAdminsStatus(row?.original.status)}
          </div>
        ),
      },

      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const editTooltip = `editTooltip-${row?.original.id}`
          const deleteTooltip = `deleteTooltip-${row?.original.id}`
          const blockTooltip = `blockTooltip-${row?.original.id}`
          const copyTooltip = `copyTooltip-${row?.original.id}`

          return (
            <div className="d-flex justify-content-center align-items-center gap-3">
              {hasPermission("USERS_UPDATE") && (
                <>
                  <div className="action-view">
                    <Link to={`/manage-users/edit/${row?.original.id}`}>
                      <img src={edit} id={editTooltip} alt="" />
                      <UncontrolledTooltip placement="top" target={editTooltip}>
                        Edit
                      </UncontrolledTooltip>
                    </Link>
                  </div>

                  {row?.original?.token &&
                    row?.original.status === "LINK_SENT" && (
                      <div
                        className="action-delete"
                        onClick={() => {
                          const url = `${process.env.REACT_APP_PUBLIC_URL}/verify-account/${row.original.token}`
                          if (url) {
                            copyToClipboard(url).then(() =>
                              toast.success("Link Copied")
                            )
                          }
                        }}
                        id={copyTooltip}
                      >
                        <img src={copy} alt="" />
                        <UncontrolledTooltip
                          placement="top"
                          target={copyTooltip}
                        >
                          Copy Link
                        </UncontrolledTooltip>
                      </div>
                    )}
                </>
              )}

              {row?.original.status !== "LINK_SENT" && (
                <>
                  {hasPermission("USERS_DELETE") && (
                    <div
                      className="action-delete"
                      onClick={() => {
                        deleteRef.current = row?.original
                        setIsToast(true)
                      }}
                    >
                      <img src={deleteIcon} id={deleteTooltip} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={deleteTooltip}
                      >
                        Delete
                      </UncontrolledTooltip>
                    </div>
                  )}

                  {hasPermission("USERS_BLOCK") && (
                    <div
                      className="action-view"
                      onClick={() => {
                        blockRef.current = row?.original
                        setIsBlockToast(true)
                      }}
                    >
                      <img src={blockIcon} id={blockTooltip} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={blockTooltip}
                      >
                        {row?.original.status === "BLOCKED"
                          ? "Unblock"
                          : "Block"}
                      </UncontrolledTooltip>
                    </div>
                  )}
                </>
              )}
            </div>
          )
        },
      },
    ]

    const canAccess = hasAnyPermission(["USERS_UPDATE", "USERS_DELETE"])
    if (canAccess) return data
    return data.filter((item) => item.id !== "actions")
  }, [hasAnyPermission])

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        data={data}
        count={count}
        isPagination={true}
        isCustomPageSize={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />

      {isToast && deleteRef && (
        <DeleteConfirmationModal
          open={isToast}
          id={deleteRef.current.id}
          setOpen={setIsToast}
          isLoading={isDeleting}
          onClose={() => {
            setIsToast(false)
            deleteRef.current = null
          }}
          title={"Delete User"}
          description="Are you sure you want to delete this user?"
          onDelete={onDelete}
          onSuccess={onActionSuccess}
        />
      )}

      {isBlockToast && blockRef && (
        <BlockAccessConfirmation
          open={isBlockToast}
          id={blockRef.current.id}
          setOpen={setIsBlockToast}
          isLoading={isBlocking || isUnBlocking}
          onClose={() => {
            setIsBlockToast(false)
            blockRef.current = null
          }}
          title={
            blockRef.current && blockRef.current.status === "BLOCKED"
              ? "Unblock Login"
              : "Block Login"
          }
          description={`Are you sure you want to ${
            blockRef.current && blockRef.current.status === "BLOCKED"
              ? "unblock"
              : "block"
          } login for this user?`}
          onBlock={
            blockRef.current && blockRef.current.status === "BLOCKED"
              ? onUnBlock
              : onBlock
          }
          onSuccess={onActionSuccess}
        />
      )}
    </>
  )
}

export default InvestorUsersTable
